import { Steps, Button, Flex, Typography, message, Modal } from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  TruckOutlined,
} from "@ant-design/icons";
import React, { useState } from "react";
import { CubeTime } from "../icon/CubeTime";
import { BoxCloseIcon, BoxOpenIcon } from "../icon";
import { BoxErrorIcon } from "../icon/BoxErrorIcon";
import { useTranslate } from "@refinedev/core";
import {
  EstadoPaso,
  FlujoPedido,
  PasoPedido,
  useEsperaDelAvance,
} from "../order-flow/flujo-pedido";

const { Step } = Steps;

type DeliveriesStepsProps = {
  currentStep: number;
  canSendReadyToDeliver: boolean;
  shipment: any;
  handleChangeStep: (
    status: string,
    shipment: any,
    grandTotal?: any,
  ) => Promise<void>;
  onRequestCancellation?: () => void;
  invoice?: any;
  isSelectMode?: boolean;
  canEnableDelivery?: boolean;
  showCancelPreview?: boolean;
  cancelTotalInclTax?: any;
  processedItems?: any;
  cancelationRequested?: boolean;
  setCancelationRequested?: (value: boolean) => void;
  orderPayment?: any;
  deliveryMethod?: string;
  /** La nota de crédito de la cancelación: va como atajo al final del flujo cancelado. */
  notaDeCredito?: any;
};

const { Text } = Typography;

export const DeliveriesSteps: React.FC<DeliveriesStepsProps> = ({
  currentStep,
  canSendReadyToDeliver = false,
  processedItems,
  shipment,
  handleChangeStep,
  onRequestCancellation,
  invoice = {},
  isSelectMode = false,
  canEnableDelivery = false,
  showCancelPreview = false,
  cancelTotalInclTax,
  cancelationRequested,
  setCancelationRequested,
  orderPayment,
  deliveryMethod,
  notaDeCredito,
}) => {
  const [open, setOpen] = useState(false);
  const [loadingStep, setLoadingStep] = useState(false);
  const { esperando, esperar, liberar } = useEsperaDelAvance(currentStep);
  const t = useTranslate();

  const stepTransparent = <span style={{ opacity: 0 }}>_</span>;

  const showModal = () => {
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  setCancelationRequested?.(orderPayment?.status === "canceled" ? true : false);

  // La factura abre el flujo: lleva al panel del pago.
  // El mismo estilo que las entregas al final del flujo del pago: ícono, verde y en negrita.
  const inicio = (
    <Flex align="flex-start" gap={6} className="flujo-pedido__entrega">
      <FileTextOutlined style={{ fontSize: 20 }} />
      <Button
        type="link"
        className="flujo-pedido__entrega-nombre"
        onClick={() => {
          window.dispatchEvent(
            new CustomEvent("openPaymentPanel", {
              detail: { paymentId: orderPayment?.paymentId },
            }),
          );
          setTimeout(() => {
            document
              .getElementById(`payment-panel-${orderPayment?.paymentId}`)
              ?.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 350);
        }}
      >
        <Text type="secondary">
          {invoice?.customId || t("orders.label.orderinvoice")}
        </Text>
      </Button>
    </Flex>
  );

  // La nota de crédito de la cancelación cierra el flujo cancelado, con el mismo estilo que
  // la factura que lo abre: lleva a su panel, adentro de Pagos.
  const numeroNota = [
    notaDeCredito?.document?.pointOfSale,
    notaDeCredito?.document?.number,
  ]
    .filter(Boolean)
    .join("-");
  const atajoNotaDeCredito = notaDeCredito ? (
    <Flex align="flex-start" gap={6} className="flujo-pedido__entrega">
      <FileTextOutlined style={{ fontSize: 16 }} />
      <Button
        type="link"
        className="flujo-pedido__entrega-nombre"
        onClick={() => {
          window.dispatchEvent(
            new CustomEvent("openPaymentPanel", {
              detail: { paymentId: orderPayment?.paymentId },
            }),
          );
          setTimeout(() => {
            document
              .getElementById(`credit-memo-${notaDeCredito.creditMemoId}`)
              ?.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 350);
        }}
      >
        <Text type="secondary">
          {t("orders.label.creditnote")}
          {numeroNota ? ` ${numeroNota}` : ""}
        </Text>
      </Button>
    </Flex>
  ) : undefined;

  // Entregado queda hecho al llegar: no tiene nada más que hacer.
  const estado = (i: number, ultimo = false): EstadoPaso =>
    currentStep > i || (ultimo && currentStep >= i)
      ? "hecho"
      : currentStep === i
      ? "actual"
      : "pendiente";

  const pasos: PasoPedido[] = [
    {
      key: "pending",
      icon: <CubeTime style={{ transform: "scale(1.2)" }} />,
      label: t("orders.tag.pendingdelivery"),
      estado: estado(0),
    },
    {
      key: "preparing",
      icon: <BoxOpenIcon />,
      label: t("orders.tag.preparingorder"),
      estado: estado(1),
    },
    {
      key: "ready_to_deliver",
      icon: <BoxCloseIcon style={{ transform: "scale(1.2)" }} />,
      label: t("orders.tag.readyToDeliver"),
      estado: estado(2),
    },
    // Un retiro en sucursal pasa de Listo para entregar a Entregado.
    ...(deliveryMethod !== "storepickup"
      ? [
          {
            key: "in_transit",
            icon: <TruckOutlined style={{ transform: "scale(1.1)" }} />,
            label: t("orders.tag.intransit"),
            estado: estado(3),
          },
        ]
      : []),
    {
      key: "delivered",
      icon: <CheckCircleOutlined style={{ transform: "scale(1.2)" }} />,
      label: t("orders.tag.delivered"),
      estado: estado(4, true),
    },
  ];

  const botonAvanzar = (texto: string, siguiente: string, disabled = false) => (
    <Button
      loading={loadingStep || esperando}
      type="primary"
      disabled={disabled}
      iconPosition="end"
      icon={<ArrowRightOutlined />}
      onClick={async () => {
        setLoadingStep(true);
        esperar();
        try {
          await handleChangeStep(siguiente, shipment);
        } catch (e) {
          liberar();
          throw e;
        } finally {
          setLoadingStep(false);
        }
      }}
    >
      {texto}
    </Button>
  );

  const boton =
    currentStep === 0
      ? {
          despuesDe: "pending",
          nodo: botonAvanzar(
            t("button.prepare"),
            "preparing",
            !canEnableDelivery,
          ),
        }
      : currentStep === 1
      ? {
          despuesDe: "preparing",
          nodo: botonAvanzar(t("button.confirmready"), "ready_to_deliver"),
        }
      : currentStep === 2
      ? {
          despuesDe: "ready_to_deliver",
          nodo: botonAvanzar(
            t("button.ship"),
            deliveryMethod === "storepickup" ? "delivered" : "in_transit",
            !canSendReadyToDeliver,
          ),
        }
      : currentStep === 3
      ? {
          despuesDe: "in_transit",
          nodo: botonAvanzar(t("buttom.Deliver"), "delivered"),
        }
      : undefined;

  return (
    <>
      {shipment?.status === "canceled" ? (
        (() => {
          // Spec XCONS-1929 etapa 5: el módulo de entrega cancelada muestra
          // la barra de progreso hasta el estado en que se encontraba la
          // entrega al momento de la cancelación (preservado en
          // `metadata.originalStatus`), seguido del step "Cancelled".
          //
          // Si no hay originalStatus (canceled previo al fix) caemos al
          // comportamiento histórico: 2 steps (Order invoice + Cancelled).
          const originalStatus = shipment?.metadata?.originalStatus as
            | string
            | undefined;
          const statusOrder = [
            "pending",
            "preparing",
            "ready_to_deliver",
            "in_transit",
            "delivered",
          ];
          const originalIdx = originalStatus
            ? statusOrder.indexOf(originalStatus)
            : -1;
          const completedSteps =
            originalIdx >= 0 ? statusOrder.slice(0, originalIdx + 1) : [];

          return (
            <FlujoPedido
              id={shipment?.shipmentId ? `entrega-${shipment.shipmentId}` : undefined}
              inicio={inicio}
              pasos={[
                ...completedSteps.map((st) => ({
                  key: st,
                  icon: <BoxOpenIcon />,
                  label: t(`orders.shipment.status.${st}`),
                  estado: "hecho" as const,
                })),
                {
                  key: "canceled",
                  icon: <BoxErrorIcon />,
                  label: t("orders.tag.canceled"),
                  estado: "error" as const,
                },
              ]}
              final={atajoNotaDeCredito}
            />
          );
        })()
      ) : !isSelectMode ? (
        <FlujoPedido
          id={shipment?.shipmentId ? `entrega-${shipment.shipmentId}` : undefined}
          inicio={inicio}
          pasos={pasos}
          boton={boton}
        />
      ) : (
        <Steps
          current={currentStep >= 0 ? currentStep + 1 : currentStep}
          className="custom-steps"
        >
          <Step
            title={
              <div className="custom-step-content">
                <div className="custom-step-text">
                  <Flex vertical>
                    <Button
                      type="link"
                      style={{ padding: 0 }}
                      onClick={() => {
                        window.dispatchEvent(
                          new CustomEvent("openPaymentPanel", {
                            detail: { paymentId: orderPayment?.paymentId },
                          }),
                        );
                        setTimeout(() => {
                          document
                            .getElementById(
                              `payment-panel-${orderPayment?.paymentId}`,
                            )
                            ?.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                        }, 350);
                      }}
                    >
                      <Text type="secondary" style={{ fontSize: 16 }}>
                        {invoice?.customId || t("orders.label.orderinvoice")}
                      </Text>
                    </Button>
                  </Flex>
                </div>
              </div>
            }
          />

          <>
            {orderPayment?.status !== "canceled" ? (
              <Step
                status={
                  cancelationRequested
                    ? "error"
                    : orderPayment?.status === "canceled"
                    ? "wait"
                    : "error"
                }
                title={
                  <div className="custom-step-content">
                    <div className="custom-step-description">
                      {stepTransparent}
                    </div>
                    <div className="custom-step-icon">
                      <CubeTime style={{ transform: "scale(1.2)" }} />
                    </div>

                    <Flex className="custom-step-title" vertical align="center">
                      {t("orders.tag.pendingdelivery")}
                      {!showCancelPreview &&
                        shipment?.status !== "canceled" && (
                          <Button
                            loading={loadingStep}
                            type="primary"
                            danger
                            iconPosition="end"
                            icon={<ArrowRightOutlined />}
                            style={{ minWidth: 130 }}
                            onClick={() => {
                              if (isSelectMode && processedItems.length === 0) {
                                message.error(t("common.messages.selectItemsToCancel"));
                              } else {
                                setCancelationRequested?.(true);
                                onRequestCancellation?.();
                              }
                            }}
                          >
                            {t("button.requestcancellation")}
                          </Button>
                        )}
                    </Flex>
                  </div>
                }
              />
            ) : (
              <Step
                status="finish"
                title={
                  <div className="custom-step-content">
                    <div className="custom-step-description">
                      {stepTransparent}
                    </div>
                    <div className="custom-step-icon">
                      <CubeTime style={{ transform: "scale(1.2)" }} />
                    </div>
                    <div className="custom-step-title">
                      {t("orders.tag.pendingdelivery")}
                    </div>
                  </div>
                }
              />
            )}

            {currentStep <= 5 && cancelationRequested !== true ? (
              <Step
                status={
                  !cancelationRequested || currentStep <= 5 ? "wait" : "error"
                }
                title={
                  <div className="custom-step-content">
                    <div className="custom-step-description">
                      {stepTransparent}
                    </div>

                    {!showCancelPreview && shipment?.status !== "canceled" ? (
                      <>
                        <div className="custom-step-icon">
                          <BoxErrorIcon />
                        </div>

                        <Flex
                          className="custom-step-title"
                          vertical
                          align="center"
                        >
                          {t("orders.pending.canceled")}
                        </Flex>
                      </>
                    ) : (
                      <>
                        <div className="custom-step-icon">
                          <BoxErrorIcon />
                        </div>

                        <Flex
                          className="custom-step-title"
                          vertical
                          align="center"
                        >
                          {t("orders.pending.canceled")}
                          <Button
                            loading={loadingStep}
                            type="primary"
                            danger
                            iconPosition="end"
                            onClick={showModal}
                            icon={
                              <ArrowRightOutlined style={{ color: "white" }} />
                            }
                          >
                            {t("button.confirmcancellation")}
                          </Button>
                        </Flex>
                      </>
                    )}

                    <div className="custom-step-title">
                      {currentStep === 0 ||
                      cancelationRequested ||
                      shipment?.status === "canceled"
                        ? stepTransparent
                        : ""}
                      {cancelationRequested || shipment?.status === "canceled"
                        ? stepTransparent
                        : ""}
                    </div>
                  </div>
                }
              />
            ) : (
              <Step
                status="error"
                title={
                  shipment?.status === "canceled" ? (
                    <div className="custom-step-content">
                      <div className="custom-step-description">
                        {stepTransparent}
                      </div>
                      <div className="custom-step-icon">
                        <BoxErrorIcon />
                      </div>

                      <div className="custom-step-title">
                        {t("orders.tag.canceled")}
                      </div>
                    </div>
                  ) : (
                    <div className="custom-step-content">
                      <div className="custom-step-description">
                        {stepTransparent}
                      </div>
                      <div className="custom-step-icon">
                        <BoxErrorIcon />
                      </div>

                      <Flex
                        className="custom-step-title"
                        vertical
                        align="center"
                      >
                        Pending canceled
                        <Button
                          loading={loadingStep}
                          type="primary"
                          danger
                          iconPosition="end"
                          onClick={showModal}
                          icon={
                            <ArrowRightOutlined style={{ color: "white" }} />
                          }
                        >
                          {t("button.confirmcancellation")}
                        </Button>
                      </Flex>
                    </div>
                  )
                }
              />
            )}

            <Step
              status={currentStep === 6 ? "error" : "wait"}
              title={
                <div className="custom-step-content">
                  <div className="custom-step-description">
                    {stepTransparent}
                  </div>

                  <div className="custom-step-icon">
                    <BoxErrorIcon style={{ stroke: "green" }} />
                  </div>

                  <div className="custom-step-title">
                    {t("orders.tag.canceled")}
                  </div>
                </div>
              }
            />

            <Step
              status={currentStep === 6 ? "finish" : "wait"}
              title={
                <div className="custom-step-content">
                  <div className="custom-step-text">
                    <Flex vertical>
                      <Text type="secondary">{t("orders.tag.invoice")}</Text>
                    </Flex>
                  </div>
                </div>
              }
            />
          </>
        </Steps>
      )}

      <Modal open={open} title={t("buttom.cancel.products")} footer={null}>
        <Flex vertical>
          <p>{t("notificacion.sureCancelProducts")}</p>

          <Flex justify="end" gap={16}>
            <Button onClick={handleCancel} icon={<ArrowLeftOutlined />}>
              {t("buttons.back")}
            </Button>
            <Button
              onClick={() => {
                if (!processedItems?.length) {
                  message.error(t("message.error.inputEmpty"));
                  return;
                }

                const itemsToCancel = processedItems.map((it: any) => ({
                  orderItemId: it.orderItemId,
                  cancelQty: Number(it.qty ?? 0),
                }));

                setLoadingStep(true);
                handleChangeStep("canceled", shipment, {
                  itemsToCancel,
                  grandTotal: cancelTotalInclTax,
                }).finally(() => setLoadingStep(false));

                handleCancel();
              }}
              type="primary"
              danger
            >
              {t("buttons.confirm")}
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};
